import { createContext, useState, useEffect, useCallback } from 'react';
import { getToken } from '../utils/auth';

export const MediaContext = createContext();

const authHeader = () => ({ 'Authorization': `Bearer ${getToken()}` });

export const MediaProvider = ({ children }) => {
  const [photos, setPhotos] = useState([]);

  const loadPhotos = useCallback(async () => {
    try {
      const res = await fetch('/api/media');
      const data = await res.json();
      if (Array.isArray(data)) {
        setPhotos(data.map(p => ({ ...p, dataUrl: p.url })));
      }
    } catch (e) {
      console.error('Failed to load media', e);
    }
  }, []);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const checkDuplicates = useCallback(async (names) => {
    try {
      const res = await fetch('/api/media/check-duplicates', {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ names }),
      });
      const data = await res.json();
      return data.duplicates || [];
    } catch {
      return [];
    }
  }, []);

  const uploadFiles = useCallback(async (files, actions = {}, folder = 'general', subfolder = '') => {
    const formData = new FormData();
    for (const f of files) formData.append('photos', f);
    try {
      const res = await fetch('/api/media/upload', {
        method: 'POST',
        headers: { ...authHeader(), 'X-Duplicate-Actions': JSON.stringify(actions), 'X-Folder': folder, 'X-Subfolder': encodeURIComponent(subfolder) },
        body: formData,
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = text;
        try { msg = JSON.parse(text).error; } catch {}
        return { success: false, error: msg };
      }
      const data = await res.json();
      await loadPhotos();
      return { success: true, count: data.photos.length, skipped: data.skipped || [] };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [loadPhotos]);

  const movePhoto = useCallback(async (id, folder, subfolder) => {
    try {
      await fetch(`/api/media/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder, subfolder }),
      });
      setPhotos(prev => prev.map(p => p.id === id ? { ...p, folder, subfolder: subfolder ?? p.subfolder } : p));
    } catch (e) {
      console.error('Failed to move photo', e);
    }
  }, []);

  const deletePhoto = useCallback(async (id) => {
    try {
      await fetch(`/api/media/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error('Failed to delete photo', e);
    }
  }, []);

  return (
    <MediaContext.Provider value={{ photos, checkDuplicates, uploadFiles, movePhoto, deletePhoto }}>
      {children}
    </MediaContext.Provider>
  );
};
